import { DropdownMenuItem } from "@/components/ui/baseUi/dropdown-menu"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { EllipsisMenu } from "@/modules/ui/components/ellipsisMenu"
import { BoxConsumableDetailsEntity } from "@modules/boxConsumable/entities/boxConsumableDetailsEntity"
import { useBoxConsumableActions } from "@modules/boxConsumable/hooks/useBoxConsumable"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { BoxStaffPermissions } from "@modules/boxStaff/gateway/boxStaffDto"
import { useBoxStaffPermission } from "@modules/boxStaff/hooks/useBoxStaffPermissions"
import { word } from "@modules/core/utils/i18n"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { Plus, SquareMinus } from "lucide-react"

type Props = {
  memberId: string
  consumableDetails: BoxConsumableDetailsEntity
  handleShowCreditModal: (consumableDetails: BoxConsumableDetailsEntity) => void
  className?: string
} & React.ComponentProps<"button">

export function MemberOfferItemConsumableMenu({
  memberId,
  consumableDetails,
  handleShowCreditModal,
  className,
  ...props
}: Props) {
  const popup = useConfirmPopup()
  const { deletePurchase, isDeletePurchasePending } = useBoxConsumableActions(memberId)

  const canEditMemberOffer = useBoxStaffPermission(BoxStaffPermissions.EDIT_MEMBER_OFFER)
  const creditSessionIsVisible =
    consumableDetails.offer.type === BoxOfferType.TicketsBook && consumableDetails.count !== null ? true : false

  const removeConsumableIsVisible =
    consumableDetails.offer.type === BoxOfferType.Millenium && !!consumableDetails.count && consumableDetails.count > 0

  if (!canEditMemberOffer || (!creditSessionIsVisible && !removeConsumableIsVisible)) {
    return <></>
  }

  const handleCreditSession = () => {
    handleShowCreditModal(consumableDetails)
  }

  const handleRemoveSubscription = () => {
    popup.show({
      title: "",
      message: word("box.member.table.offer.item.popup.removeMessage"),
      classNames: {
        dialogContent: "w-104",
      },
      accept: () => {
        if (!isDeletePurchasePending) {
          deletePurchase(consumableDetails.id)
            .then(() => {
              toast.success(word("box.member.table.offer.item.toast.success"), {
                classNames: successToastClassName(),
              })
            })
            .catch(() => {
              toast.error(word("box.member.table.offer.item.toast.error"), {
                classNames: errorToastClassName(),
              })
            })
        }
      },
      footerProps: {
        confirmText: word("global.delete"),
      },
    })
  }

  return (
    <EllipsisMenu propsContent={{ side: "bottom" }} className={className} {...props}>
      {creditSessionIsVisible && (
        <DropdownMenuItem
          key="paste"
          onClick={() => {
            handleCreditSession()
          }}
        >
          <Plus color="currentColor" strokeWidth={1} size={16} />
          {word("box.member.table.offer.item.menu.creditSession")}
        </DropdownMenuItem>
      )}

      {removeConsumableIsVisible && (
        <DropdownMenuItem
          key="remove"
          variant="destructive"
          onClick={() => {
            handleRemoveSubscription()
          }}
        >
          <SquareMinus color="currentColor" strokeWidth={1} size={16} />
          {word("box.member.table.offer.item.menu.removeSubscription")}
        </DropdownMenuItem>
      )}
    </EllipsisMenu>
  )
}
